import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ManagerDashboard.css';
import TrainingViewer from './TrainingViewer';
import { downloadRecordedCertificate } from '../utils/certificateGenerator';

export function ManagerDashboard({ userId, userEmail }) {
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [managerTrainings, setManagerTrainings] = useState([]);
  const [managerProgress, setManagerProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [showNewTeamForm, setShowNewTeamForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showAddMembersForm, setShowAddMembersForm] = useState(false);
  const [updatingTrainingId, setUpdatingTrainingId] = useState(null);
  const [viewingTraining, setViewingTraining] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [certificateName, setCertificateName] = useState('');
  const [pendingTraining, setPendingTraining] = useState(null);
  const [downloadingCertId, setDownloadingCertId] = useState(null);
  const [showCertificateMessage, setShowCertificateMessage] = useState(false);
  const [selectedCertificateTraining, setSelectedCertificateTraining] = useState(null);
  const [allTracks, setAllTracks] = useState([]);
  const [userTracks, setUserTracks] = useState({});
  const [selectedMemberForTracks, setSelectedMemberForTracks] = useState(null);
  const [showTrackManager, setShowTrackManager] = useState(false);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberNameEdit, setMemberNameEdit] = useState({});

  useEffect(() => {
    loadUserTeams();
    loadManagerTrainings();
    loadAllTracks();

    // Fetch user info
    if (userId) {
      fetch(`/api/training/user-info?userId=${userId}`)
        .then(res => res.ok && res.json())
        .then(data => setUser(data))
        .catch(err => console.log('Failed to fetch user info:', err));
    }
  }, [userId]);

  useEffect(() => {
    if (selectedTeam) {
      loadTeamDashboard(selectedTeam.id);
      loadManagerTrainings();

      // Auto-refresh team data every 10 seconds to show real-time updates
      const interval = setInterval(() => {
        loadTeamDashboard(selectedTeam.id);
        loadManagerTrainings();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [selectedTeam]);

  const loadManagerTrainings = async () => {
    try {
      const [trainingsRes, progressRes] = await Promise.all([
        fetch(`/api/training/member-trainings?userId=${userId}`),
        fetch(`/api/training/member-progress?userId=${userId}`)
      ]);
      if (trainingsRes.ok) {
        const trainings = await trainingsRes.json();
        setManagerTrainings(trainings);
      }
      if (progressRes.ok) {
        const progress = await progressRes.json();
        setManagerProgress(progress);
      }
    } catch (err) {
      console.error('Failed to load manager trainings:', err);
    }
  };

  const loadUserTeams = async () => {
    try {
      const response = await fetch(`/api/training/user-teams?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to load teams');
      const data = await response.json();
      setTeams(data);
      if (data.length > 0) {
        setSelectedTeam(data[0]);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadTeamDashboard = async (teamId) => {
    try {
      const response = await fetch(`/api/training/team-dashboard?teamId=${teamId}&userId=${userId}`);
      if (!response.ok) throw new Error('Failed to load dashboard');
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartTraining = (training) => {
    setPendingTraining(training);
    setShowNameDialog(true);
  };

  const handleConfirmName = () => {
    if (!certificateName.trim()) {
      alert('Please enter your full name');
      return;
    }
    localStorage.setItem(`training_name_${userId}`, certificateName.trim());
    setShowNameDialog(false);
    const training = pendingTraining;
    setPendingTraining(null);
    handleUpdateTrainingStatus(training, 'in-progress');
    setViewingTraining(training);
  };

  const handleUpdateTrainingStatus = async (training, newStatus, result = {}) => {
    setUpdatingTrainingId(training.id);
    try {
      const response = await fetch('/api/training/update-training-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          moduleId: training.module_id,
          teamId: training.team_id,
          status: newStatus,
          quizScore: result.quizScore
        })
      });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to update status');
      }

      // Issue the (server-recorded) certificate once the training is completed
      if (newStatus === 'completed') {
        const certificateName = result.certificateName || localStorage.getItem(`training_name_${userId}`);
        try {
          await fetch('/api/training/create-training-certificate', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              moduleId: training.module_id,
              certificateName
            })
          }).then(r => r.ok && r.json()).then(cert => {
            if (cert?.code) localStorage.setItem(`training_cert_${userId}_${training.module_id}`, JSON.stringify(cert));
          });
        } catch (certErr) {
          console.warn('Certificate creation failed (non-blocking):', certErr);
        }
      }

      loadManagerTrainings();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingTrainingId(null);
    }
  };

  const handleDownloadCertificate = (training) => {
    const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User';
    downloadRecordedCertificate({
      moduleId: training.module_id,
      title: training.module_title || training.title,
      completedAt: training.completed_at,
      fallbackName: localStorage.getItem(`training_name_${userId}`) || userName,
    });
  };

  const handleRestartTraining = async (training) => {
    if (window.confirm(`Are you sure you want to restart "${training.module_title}"? This will reset your progress.`)) {
      await handleUpdateTrainingStatus(training, 'pending');
    }
  };

  const handleDeleteTraining = async (moduleId, moduleTitle) => {
    if (!window.confirm(`⚠️ Are you sure you want to DELETE "${moduleTitle}"?\n\nThis will:\n• Remove all assignments for this training\n• Cannot be undone\n\nContinue?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/training/delete-training?moduleId=${moduleId}`, {
        method: 'DELETE',
        headers: {
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete training');
      }

      const result = await response.json();
      alert(`✓ ${result.message}`);

      // Reload data
      loadManagerTrainings();
      if (selectedTeam) {
        loadTeamDashboard(selectedTeam.id);
      }
    } catch (err) {
      alert(`✗ Error: ${err.message}`);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`⚠️ Are you sure you want to DELETE the team "${teamName}"?\n\nThis will:\n• Remove all team members\n• Remove all training assignments\n• Cannot be undone\n\nContinue?`)) {
      return;
    }

    try {
      console.log('[Manager] Deleting team:', { teamId, teamName });
      const response = await fetch(`/api/training/delete-team?teamId=${teamId}`, {
        method: 'DELETE',
        headers: {
        }
      });

      console.log('[Manager] Delete response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Manager] Delete error response:', errorData);
        throw new Error(errorData.error || `Failed to delete team (HTTP ${response.status})`);
      }

      const result = await response.json();
      console.log('[Manager] Delete success:', result);
      alert(`✓ ${result.message}`);

      // Reset and reload
      setSelectedTeam(null);
      loadUserTeams();
      loadManagerTrainings();
    } catch (err) {
      console.error('[Manager] Delete error:', err);
      alert(`✗ Error: ${err.message}`);
    }
  };

  const loadAllTracks = async () => {
    try {
      const response = await fetch('/api/training/tracks');
      if (response.ok) {
        const tracks = await response.json();
        setAllTracks(tracks);
      }
    } catch (err) {
      console.error('Failed to load tracks:', err);
    }
  };

  const loadMemberTracks = async (memberId) => {
    setLoadingTracks(true);
    try {
      console.log('[Tracks] Loading member tracks for:', memberId);
      const response = await fetch(`/api/training/user-tracks?userId=${encodeURIComponent(memberId)}`);
      console.log('[Tracks] Member tracks response:', response.status);
      if (response.ok) {
        const tracks = await response.json();
        console.log('[Tracks] Tracks data received:', tracks);
        const trackIds = tracks.map(t => t.id);
        console.log('[Tracks] Setting userTracks for', memberId, ':', trackIds);
        setUserTracks(prev => ({
          ...prev,
          [memberId]: trackIds
        }));
      } else {
        console.error('[Tracks] Failed to load tracks, status:', response.status);
      }
    } catch (err) {
      console.error('[Tracks] Failed to load member tracks:', err);
    } finally {
      setLoadingTracks(false);
    }
  };

  const handleToggleTrack = async (memberId, trackId, hasAccess) => {
    try {
      console.log('[Tracks] Toggling track:', { memberId, trackId, hasAccess, action: hasAccess ? 'remove' : 'add' });

      if (hasAccess) {
        // Remove track
        const url = `/api/training/remove-track?userId=${encodeURIComponent(memberId)}&trackId=${encodeURIComponent(trackId)}`;
        console.log('[Tracks] Removing track from:', url);

        const response = await fetch(url, {
          method: 'DELETE',
        });

        console.log('[Tracks] Remove response:', response.status, response.statusText);
        const result = await response.json();
        console.log('[Tracks] Remove result:', result);

        if (!response.ok) throw new Error(result.error || 'Failed to remove track');
      } else {
        // Add track
        console.log('[Tracks] Assigning track:', { userId: memberId, trackId });

        const response = await fetch('/api/training/assign-track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: memberId, trackId })
        });

        console.log('[Tracks] Assign response:', response.status);
        const result = await response.json();
        console.log('[Tracks] Assign result:', result);

        if (!response.ok) throw new Error(result.error || 'Failed to assign track');
      }

      console.log('[Tracks] Reloading member tracks after toggle');
      // Reload member tracks
      await loadMemberTracks(memberId);
      console.log('[Tracks] Toggle completed successfully');
    } catch (err) {
      console.error('[Tracks] Error toggling track:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!selectedTeam) return;
    const label = member.display_name || member.user_email;
    if (!window.confirm(`Remove ${label} from "${selectedTeam.name}"?\n\nTheir open assignments for this team are removed; completed training records are kept.`)) return;
    try {
      const response = await fetch(`/api/training/team-members?teamId=${encodeURIComponent(selectedTeam.id)}&memberId=${encodeURIComponent(member.user_id)}`, { method: 'DELETE' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Failed to remove member');
      loadTeamDashboard(selectedTeam.id);
      loadUserTeams();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const startEditingMember = (member) => {
    setEditingMemberId(member.user_id);
    setMemberNameEdit({ [member.user_id]: member.display_name || member.user_email });
  };

  const cancelEditingMember = () => {
    setEditingMemberId(null);
    setMemberNameEdit({});
  };

  const saveMemberName = async (member) => {
    const newName = memberNameEdit[member.user_id];
    if (!newName || !newName.trim()) {
      alert('Please enter a valid name');
      return;
    }

    try {
      const response = await fetch('/api/training/update-member-name', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: member.user_id,
          displayName: newName.trim()
        })
      });

      if (!response.ok) throw new Error('Failed to update member name');

      // Update the dashboard data
      if (selectedTeam) {
        loadTeamDashboard(selectedTeam.id);
      }
      setEditingMemberId(null);
      setMemberNameEdit({});
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const myTrainingsSection = managerTrainings.length > 0 && (

            <section className="manager-trainings-section">
              <h2>{t('manager.myTrainings')}</h2>
              <div className="manager-trainings-grid">
                {managerTrainings.map(training => (
                  <div
                    key={training.id}
                    className="manager-training-card"
                    onClick={() => setViewingTraining(training)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="training-header-mini">
                      <h3>{training.module_title}</h3>
                      <span className={`status-badge status-${training.status}`}>
                        {training.status === 'pending' ? t('manager.status.notStarted') :
                         training.status === 'in-progress' ? t('manager.status.inProgress') :
                         training.status === 'completed' ? t('manager.status.completed') : training.status}
                      </span>
                    </div>
                    <p className="training-team">{t('player.teamLabel')} {training.team_name}</p>
                    <div className="training-dates-mini">
                      <small>{training.due_date ? `${t('player.dueLabel')} ${new Date(training.due_date).toLocaleDateString()}` : t('manager.selfPaced')}</small>
                    </div>
                    <div className="training-actions-mini">
                      {training.status !== 'completed' && (
                        <>
                          {training.status === 'pending' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartTraining(training);
                              }}
                              className="btn-mini btn-start-mini"
                              disabled={updatingTrainingId === training.id}
                            >
                              {updatingTrainingId === training.id ? '...' : t('player.startLesson')}
                            </button>
                          )}
                          {training.status === 'in-progress' && (
                            <span style={{ fontSize: '0.85rem', color: '#f59e0b' }}>In Progress...</span>
                          )}
                          {training.team_id && <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTraining(training.module_id, training.module_title);
                            }}
                            className="btn-mini"
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              padding: '6px 12px',
                              fontSize: '12px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                            title="Delete training"
                          >
                            🗑️
                          </button>}
                        </>
                      )}
                      {training.status === 'completed' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCertificateTraining(training);
                              setShowCertificateMessage(true);
                            }}
                            className="btn-mini btn-download-mini"
                            style={{
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              padding: '6px 12px',
                              fontSize: '12px'
                            }}
                          >
                            📜
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestartTraining(training);
                            }}
                            className="btn-mini btn-restart-mini"
                            disabled={updatingTrainingId === training.id}
                          >
                            {updatingTrainingId === training.id ? '...' : '🔄 ' + t('player.resumeLesson')}
                          </button>
                          {training.team_id && <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTraining(training.module_id, training.module_title);
                            }}
                            className="btn-mini"
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              padding: '6px 12px',
                              fontSize: '12px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                            title="Delete training"
                          >
                            🗑️
                          </button>}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          
  );

  const modals = (
    <>
      {viewingTraining && (
        <TrainingViewer
          training={viewingTraining}
          userId={userId}
          onClose={() => setViewingTraining(null)}
          defaultCertificateName={localStorage.getItem(`training_name_${userId}`) || ''}
          onComplete={(result) => {
            handleUpdateTrainingStatus(viewingTraining, 'completed', result || {});
            setViewingTraining(null);
          }}
        />
      )}

      {showCertificateMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1f2937' }}>
              📜 Certificate of Completion
            </h2>
            <p style={{ fontSize: '16px', color: '#4b5563', marginBottom: '20px', lineHeight: '1.6' }}>
              Congratulations! You have successfully completed <strong>{selectedCertificateTraining?.module_title}</strong>.
            </p>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              Your certificate is ready to download and print.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  const userName = user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email || 'User';
                  downloadRecordedCertificate({
                    moduleId: selectedCertificateTraining?.module_id,
                    title: selectedCertificateTraining?.module_title,
                    completedAt: selectedCertificateTraining?.completed_at,
                    fallbackName: localStorage.getItem(`training_name_${userId}`) || userName,
                  });
                  setShowCertificateMessage(false);
                  setSelectedCertificateTraining(null);
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                📥 Download Certificate
              </button>
              <button
                onClick={() => {
                  setShowCertificateMessage(false);
                  setSelectedCertificateTraining(null);
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewTeamForm && (
        <NewTeamForm userId={userId} onTeamCreated={() => {
          setShowNewTeamForm(false);
          loadUserTeams();
        }} />
      )}

      {showNameDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
              What name should appear on your certificate?
            </h2>
            <input
              type="text"
              placeholder="Enter your full name"
              value={certificateName}
              onChange={(e) => setCertificateName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleConfirmName()}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxSizing: 'border-box',
                fontSize: '14px'
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleConfirmName}
                disabled={!certificateName.trim()}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: certificateName.trim() ? 'pointer' : 'not-allowed',
                  opacity: certificateName.trim() ? 1 : 0.5,
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                Continue
              </button>
              <button
                onClick={() => {
                  setShowNameDialog(false);
                  setCertificateName('');
                  setPendingTraining(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#999',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (loading) return <div className="manager-loading">{t('common.loading')}</div>;

  if (teams.length === 0) {
    return (
      <div className="manager-container">
        <div className="manager-header">
          <h1>{t('manager.title')}</h1>
          <p>{t('manager.subtitle')}</p>
        </div>
        <div className="no-teams">
          <p>{t('messages.noTeams')}</p>
          <button onClick={() => setShowNewTeamForm(true)} className="create-team-btn">
            {t('manager.newTeam')}
          </button>
        </div>

        <div className="manager-layout"><main className="manager-main">{myTrainingsSection}</main></div>

        {modals}
      </div>
    );
  }

  return (
    <div className="manager-container">
      <div className="manager-header">
        <div className="header-left">
          <h1>{t('manager.title')}</h1>
          <p>{t('manager.subtitle')}</p>
        </div>
        <div className="header-right">
          <div className="teams-dropdown">
            <h3>{t('manager.yourTeams')}</h3>
            <div className="teams-list-horizontal">
              {teams.map(team => (
                <div
                  key={team.id}
                  className={`team-item-horizontal ${selectedTeam?.id === team.id ? 'active' : ''}`}
                  onClick={() => setSelectedTeam(team)}
                >
                  <div className="team-name">{team.name}</div>
                  <div className="team-meta-compact">
                    <span>{team.member_count || 0} {t('manager.members')}</span>
                    <span className={`overdue ${team.overdue_count > 0 ? 'warning' : ''}`}>
                      {team.overdue_count || 0} {t('manager.overdue')}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTeam(team.id, team.name);
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      marginLeft: '8px'
                    }}
                    title="Delete team"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setShowNewTeamForm(true)} className="new-team-btn-header">
              + {t('manager.newTeam')}
            </button>
          </div>
        </div>
      </div>

      <div className="manager-layout">
        {/* Main: Dashboard */}
        <main className="manager-main">
          {error && <div className="error-message">{error}</div>}

          {myTrainingsSection}

          {dashboardData && (
            <>
              {/* Summary Cards */}
              <section className="summary-cards">
                <div className="card">
                  <h3>{t('manager.teamMembers')}</h3>
                  <div className="card-value">{dashboardData.summary?.actual_members || 0}</div>
                  <p className="card-label">{t('manager.totalMembers')}</p>
                </div>

                <div className="card">
                  <h3>{t('manager.complianceRate')}</h3>
                  <div className={`card-value ${(dashboardData.summary?.compliance_percentage || 0) >= 80 ? 'good' : 'warning'}`}>
                    {dashboardData.summary?.compliance_percentage || 0}%
                  </div>
                  <p className="card-label">{t('manager.trainingCompleted')}</p>
                </div>

                <div className="card danger">
                  <h3>{t('manager.overdueTraining')}</h3>
                  <div className="card-value">{dashboardData.overdueTrainings?.length || 0}</div>
                  <p className="card-label">{t('manager.assignments')}</p>
                </div>

                <div className="card">
                  <h3>{t('manager.fullCompliant')}</h3>
                  <div className="card-value">{dashboardData.summary?.fully_compliant_members || 0}</div>
                  <p className="card-label">{t('manager.fullyCompliantMembers')}</p>
                </div>
              </section>

              {/* Assigned Trainings Section */}
              <section className="assigned-trainings" style={{ marginTop: '2rem' }}>
                <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>📚 Assigned Trainings</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                  {dashboardData.teamAssignments && dashboardData.teamAssignments.length > 0 ? (
                    dashboardData.teamAssignments.map(training => (
                      <div key={training.module_id} style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '1rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: '#1f2937', fontSize: '1rem' }}>
                          {training.module_title || training.title || 'Untitled Training'}
                        </h3>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                          <p style={{ margin: '0.25rem 0' }}>👥 {training.assigned_count || 0} members assigned</p>
                          <p style={{ margin: '0.25rem 0' }}>✓ {training.completed_count || 0} completed</p>
                          <p style={{ margin: '0.25rem 0' }}>📅 Due: {training.due_date ? new Date(training.due_date).toLocaleDateString() : 'N/A'}</p>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          background: '#e5e7eb',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${training.assigned_count > 0 ? Math.round((training.completed_count / training.assigned_count) * 100) : 0}%`,
                            background: '#10b981',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#999' }}>
                          {training.assigned_count > 0 ? Math.round((training.completed_count / training.assigned_count) * 100) : 0}% complete
                        </p>
                      </div>
                    ))
                  ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#999', padding: '2rem' }}>
                      No trainings assigned yet. Click "Assign Training" to get started!
                    </div>
                  )}
                </div>
              </section>

              {/* Member Status Table */}
              <section className="member-status">
                <div className="section-header">
                  <h2>{t('manager.teamMemberStatus')}</h2>
                  <div className="button-group">
                    <button onClick={() => setShowAddMembersForm(true)} className="assign-btn">
                      {t('manager.addMembers')}
                    </button>
                    <button onClick={() => setShowAssignForm(true)} className="assign-btn">
                      {t('manager.assignTraining')}
                    </button>
                    <button onClick={() => setShowTrackManager(true)} className="assign-btn" style={{ background: '#8b5cf6', color: 'white' }}>
                      {t('manager.manageTracks')}
                    </button>
                  </div>
                </div>

                <div className="table-container">
                  <table className="member-table">
                    <thead>
                      <tr>
                        <th>{t('manager.tableHeaders.member')}</th>
                        <th>{t('manager.tableHeaders.assigned')}</th>
                        <th>{t('manager.tableHeaders.completed')}</th>
                        <th>{t('manager.tableHeaders.overdue')}</th>
                        <th>{t('manager.tableHeaders.progress')}</th>
                        <th>{t('manager.tableHeaders.nextDue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.memberStatus?.map(member => (
                        <tr key={member.user_id}>
                          <td className="member-name">
                            {editingMemberId === member.user_id ? (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={memberNameEdit[member.user_id] || ''}
                                  onChange={(e) => setMemberNameEdit({ ...memberNameEdit, [member.user_id]: e.target.value })}
                                  style={{
                                    flex: 1,
                                    padding: '0.5rem',
                                    border: '1px solid #3b82f6',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem'
                                  }}
                                  autoFocus
                                />
                                <button
                                  onClick={() => saveMemberName(member)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✓ Save
                                </button>
                                <button
                                  onClick={cancelEditingMember}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✕ Cancel
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => startEditingMember(member)}
                                style={{
                                  cursor: 'pointer',
                                  padding: '0.5rem',
                                  borderRadius: '4px',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                {member.display_name || member.user_email}
                                {member.display_name && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{member.user_email}</div>}
                              </div>
                            )}
                            {member.user_id !== userId && editingMemberId !== member.user_id && (
                              <button
                                onClick={() => handleRemoveMember(member)}
                                title="Remove from team"
                                style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem' }}
                              >
                                ✕ Remove
                              </button>
                            )}
                          </td>
                          <td>{member.assigned_trainings || 0}</td>
                          <td className="completed">{member.completed_trainings || 0}</td>
                          <td className={`overdue ${member.overdue_trainings > 0 ? 'warning' : ''}`}>
                            {member.overdue_trainings || 0}
                          </td>
                          <td>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${member.completion_percentage || 0}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{Math.round(member.completion_percentage || 0)}%</span>
                          </td>
                          <td className="due-date">
                            {member.next_due_date
                              ? new Date(member.next_due_date).toLocaleDateString()
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Overdue Assignments */}
              {dashboardData.overdueTrainings?.length > 0 && (
                <section className="overdue-section">
                  <h2>⚠️ Overdue Assignments</h2>
                  <div className="overdue-list">
                    {dashboardData.overdueTrainings?.map((item, idx) => (
                      <div key={idx} className="overdue-item">
                        <div className="item-info">
                          <strong>{item.user_name || item.user_email}</strong>
                          <p>{item.module_title || item.module_id}</p>
                          <p>Due: {new Date(item.due_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {showTrackManager && (
                <div style={{
                  position: 'fixed',
                  top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '2rem',
                    width: '90%',
                    maxWidth: '600px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    maxHeight: '80vh',
                    overflowY: 'auto'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <h2 style={{ margin: 0 }}>{t('manager.manageTracks')}</h2>
                      <button onClick={() => setShowTrackManager(false)} style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer'
                      }}>✕</button>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                        {t('manager.forms.selectTeamMember')}
                      </label>
                      <select onChange={(e) => {
                        setSelectedMemberForTracks(e.target.value);
                        if (e.target.value) loadMemberTracks(e.target.value);
                      }} style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}>
                        <option value="">{t('manager.forms.selectMemberPlaceholder')}</option>
                        {dashboardData?.memberStatus?.map(member => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.display_name ? `${member.display_name} (${member.user_email})` : member.user_email}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedMemberForTracks && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '1rem', fontWeight: '600' }}>
                          Available Tracks:
                        </label>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                          {allTracks.map(track => {
                            const memberTracks = userTracks[selectedMemberForTracks] || [];
                            const hasAccess = memberTracks.includes(track.id);
                            return (
                              <div key={track.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '1rem',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                background: hasAccess ? '#f0fdf4' : 'white',
                                cursor: 'pointer'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={hasAccess}
                                  onChange={() => handleToggleTrack(selectedMemberForTracks, track.id, hasAccess)}
                                  style={{ marginRight: '1rem', cursor: 'pointer', width: '18px', height: '18px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                    {track.icon} {track.name}
                                  </div>
                                  <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                    {track.description}
                                  </div>
                                </div>
                                {hasAccess && <span style={{ color: '#10b981', fontSize: '1.2rem' }}>✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <button onClick={() => setShowTrackManager(false)} style={{
                        background: '#e5e7eb',
                        color: '#1f2937',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {showAssignForm && selectedTeam && (
            <AssignTrainingForm
              teamId={selectedTeam.id}
              userId={userId}
              teamMembers={dashboardData?.memberStatus || []}
              onAssigned={() => {
                setShowAssignForm(false);
                loadTeamDashboard(selectedTeam.id);
                loadManagerTrainings();
              }}
            />
          )}

          {showAddMembersForm && (
            <AddTeamMembersForm
              teamId={selectedTeam.id}
              onMembersAdded={() => {
                setShowAddMembersForm(false);
                loadTeamDashboard(selectedTeam.id);
              }}
            />
          )}
        </main>
      </div>

      {modals}
    </div>
  );
}

function NewTeamForm({ userId, onTeamCreated }) {
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [track, setTrack] = useState('compliance-team');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tracks = [
    { id: 'compliance-team', name: '👥 Compliance Team', description: 'Required training for compliance team members' },
    { id: 'certification', name: '🎯 Professional Certification', description: 'CAMS exam preparation plus core compliance courses' },
    { id: 'compliance-program', name: '🏢 Compliance Program Building', description: 'All courses, for teams building or running a compliance program' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) {
      setError('Team name required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/training/team-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teamName,
          teamDescription,
          track,
          managerId: userId
        })
      });

      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to create team');
      }
      onTeamCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Create New Team</h2>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Team Name"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            required
          />
          <textarea
            placeholder="Team Description (optional)"
            value={teamDescription}
            onChange={(e) => setTeamDescription(e.target.value)}
            rows={3}
          />
          <label>
            Primary Training Track:
            <select value={track} onChange={(e) => setTrack(e.target.value)}>
              {tracks.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '-0.5rem' }}>
            {tracks.find(t => t.id === track)?.description}
          </p>
          <div className="form-actions">
            <button type="submit" disabled={loading}>Create Team</button>
            <button type="button" onClick={onTeamCreated} className="cancel">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignTrainingForm({ teamId, userId, onAssigned, teamMembers }) {
  const { t } = useTranslation();
  const [moduleId, setModuleId] = useState('');
  const [dueDays, setDueDays] = useState('30');
  const [assignmentType, setAssignmentType] = useState('team'); // 'team' or 'individual'
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadModules();
  }, [teamId]);

  const loadModules = async () => {
    try {
      const response = await fetch(`/api/training/team-modules?teamId=${teamId}`);
      if (!response.ok) throw new Error('Failed to load modules');
      const data = await response.json();
      setModules(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!moduleId) {
      setError('Module required');
      return;
    }

    if (assignmentType === 'individual' && selectedMembers.length === 0) {
      setError('Please select at least one member');
      return;
    }

    setLoading(true);
    try {
      if (assignmentType === 'team') {
        // Assign to entire team
        const response = await fetch('/api/training/assign-training', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            teamId,
            moduleId,
            dueDays: parseInt(dueDays)
          })
        });

        if (!response.ok) {
          const e = await response.json().catch(() => ({}));
          throw new Error(e.error || 'Failed to assign training');
        }
      } else {
        // Assign to individual members
        for (const memberId of selectedMembers) {
          const response = await fetch('/api/training/assign-to-member', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: memberId,
              moduleId,
              teamId,
              dueDays: parseInt(dueDays)
            })
          });

          if (!response.ok) throw new Error(`Failed to assign training to member`);
        }
      }

      onAssigned();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <h2>{t('manager.assignTraining')}</h2>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} required>
            <option value="">{t('manager.forms.selectTrainingModule')}</option>
            {modules.map(m => (
              <option key={m.id} value={m.id}>{m.title}</option>
            ))}
          </select>

          <label>
            {t('manager.forms.dueInDays')}
            <input
              type="number"
              value={dueDays}
              onChange={(e) => setDueDays(e.target.value)}
              min="1"
              max="365"
            />
          </label>

          <label style={{ display: 'block', marginBottom: '1rem', fontWeight: '500', color: '#1f2937' }}>
            Assign To:
          </label>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
              <input
                type="radio"
                name="assignmentType"
                value="team"
                checked={assignmentType === 'team'}
                onChange={(e) => {
                  setAssignmentType(e.target.value);
                  setSelectedMembers([]);
                }}
                style={{ marginRight: '0.5rem', cursor: 'pointer', width: 'auto' }}
              />
              Entire Team
            </label>
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
              <input
                type="radio"
                name="assignmentType"
                value="individual"
                checked={assignmentType === 'individual'}
                onChange={(e) => setAssignmentType(e.target.value)}
                style={{ marginRight: '0.5rem', cursor: 'pointer', width: 'auto' }}
              />
              Individual Members
            </label>
          </div>

          {assignmentType === 'individual' && (
            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f3f4f6', borderRadius: '6px', maxHeight: '300px', overflowY: 'auto' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500', fontSize: '0.9rem', color: '#1f2937' }}>
                Select Members:
              </label>
              {teamMembers && teamMembers.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {teamMembers.map(member => (
                    <label key={member.user_id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '0.5rem', borderRadius: '4px', background: 'white', transition: 'background 0.2s ease' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.user_id)}
                        onChange={() => handleMemberToggle(member.user_id)}
                        style={{ marginRight: '0.75rem', cursor: 'pointer', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                        {member.display_name || member.user_email}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No team members available</p>
              )}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" disabled={loading}>
              {loading ? 'Assigning...' : 'Assign Training'}
            </button>
            <button type="button" onClick={onAssigned} className="cancel">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddTeamMembersForm({ teamId, onMembersAdded }) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const list = emails.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
    if (list.length === 0) {
      setError(t('manager.forms.emailsRequired'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/training/team-members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId, emails: list })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to add members');
      if ((data.notFound || []).length || (data.rejected || []).length) {
        setResult(data);
      } else {
        onMembersAdded();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t('manager.forms.addTeamMembers')}</h2>
        {error && <div className="error">{error}</div>}
        {result ? (
          <div>
            <p>{t('manager.forms.membersAdded', { count: result.addedMembers || 0 })}</p>
            {(result.notFound || []).length > 0 && (
              <p style={{ color: '#b45309' }}>{t('manager.forms.emailsNotFound')} {result.notFound.join(', ')}</p>
            )}
            {(result.rejected || []).length > 0 && (
              <p style={{ color: '#b91c1c' }}>{t('manager.forms.emailsOtherCompany')} {result.rejected.join(', ')}</p>
            )}
            <div className="form-actions">
              <button type="button" onClick={onMembersAdded}>{t('common.close')}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label>
              {t('manager.forms.memberEmails')}
              <textarea
                placeholder={t('manager.forms.memberEmailsPlaceholder')}
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={4}
                required
              />
            </label>
            <p className="help-text" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '-0.5rem' }}>
              {t('manager.forms.memberEmailsHelp')}
            </p>
            <div className="form-actions">
              <button type="submit" disabled={loading}>{loading ? '…' : t('manager.addMembers')}</button>
              <button type="button" onClick={onMembersAdded} className="cancel">{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ManagerDashboard;
